'use client';

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCentrifugo } from '@/contexts/CentrifugoContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { useProfileData } from '@/queries/useProfileQueries';
import { queryKeys } from '@/lib/queryClient';
import { useCatalog } from '@/screens/AchievementsScreen/hooks/useCatalog';
import { useOpenAchievementSheet } from '@/stores/achievementSheetStore';
import { useOpenClaimSheet } from '@/stores/claimSheetStore';
import { AchievementUnlockToast } from '@/components/AchievementUnlockToast';
import { loadSounds, playSfx } from '@/lib/audioEngine';
import type { CatalogReward, CatalogResponse } from '@/services/generated/Api';

const UNLOCK_SFX = 'achievement_unlock';

interface UnlockMessage {
  type?: string;
  key?: string;
  unlockedAt?: string;
  reward?: CatalogReward;
  /** Whether the bonus was actually credited (false → unlocked but reward locked behind deposits). */
  claimed?: boolean;
  hiddenUntilUnlocked?: boolean;
}

/**
 * Subscribe to `${prefix}_user#${userId}` for `achievement_unlocked` messages
 * and surface a liquid-glass toast. Tapping the toast jumps to the profile and
 * opens the achievement's detail sheet — UNLESS the user is mid-roulette
 * (`case-roulette`), where we never yank them off the spin.
 *
 * Backfill never publishes, so this only fires on live unlocks.
 */
export const useAchievementUnlockedSubscription = () => {
  const { subscribe, isConnected, channelPrefix } = useCentrifugo();
  const queryClient = useQueryClient();
  const profile = useProfileData();
  const userId = profile?.id;
  const openSheet = useOpenAchievementSheet();
  const openClaimSheet = useOpenClaimSheet();
  useCatalog();

  // Read the LIVE navigation surface inside the toast click handler (which is
  // created at unlock time and may outlive a navigation change).
  const navigation = useNavigation();
  const navRef = useRef(navigation);
  navRef.current = navigation;

  // warm-decode the unlock fanfare so it fires the instant the toast pops
  useEffect(() => { void loadSounds([UNLOCK_SFX]).catch(() => undefined); }, []);

  useEffect(() => {
    if (!isConnected || !userId) return;

    const channel = channelPrefix ? `${channelPrefix}_user#${userId}` : `user#${userId}`;

    const unsubscribe = subscribe(channel, (data) => {
      void (async () => {
        const event = data as UnlockMessage;
        if (event?.type !== 'achievement_unlocked') return;

        const key = String(event.key ?? '');

        // Hidden rules carry '???' in the cached catalog until the unlock;
        // refetch FIRST so the title/icon are the de-redacted values.
        if (event.hiddenUntilUnlocked) {
          await queryClient.refetchQueries({
            queryKey: queryKeys.achievementsCatalog,
            exact: true,
          });
        }

        const catalog = queryClient.getQueryData<CatalogResponse>(queryKeys.achievementsCatalog);
        const item = catalog?.items.find((i) => i.key === key);
        const lang = (typeof document !== 'undefined' ? document.documentElement.lang : '').toLowerCase();
        const localeKey = lang.startsWith('en') ? 'en' : lang.startsWith('uz') ? 'uz' : 'ru';
        const title = item?.i18n?.[localeKey]?.title ?? item?.i18n?.ru?.title;
        const displayTitle = title && title !== '???' ? title : '🏆';
        const credits = event.reward?.credits ?? item?.reward?.credits ?? 0;
        const xp = event.reward?.xp ?? item?.reward?.xp ?? 0;
        const claimed = event.claimed ?? true;
        const iconFile = item?.iconFile ?? '';

        playSfx(UNLOCK_SFX, 0.6); // fanfare as the unlock toast pops

        toast.custom(
          (id) => (
            <AchievementUnlockToast
              iconFile={iconFile}
              title={displayTitle}
              rewardCredits={credits}
              rewardXp={xp}
              claimed={claimed}
              onClick={() => {
                toast.dismiss(id);
                // Never interrupt an in-flight case roulette.
                if (navRef.current.getCurrentScreen?.().screen === 'case-roulette') return;
                if (!claimed) {
                  // Reward locked — take them to the catalogue and open the claim sheet.
                  navRef.current.navigate('achievements');
                  openClaimSheet();
                  return;
                }
                navRef.current.changeTab('profile');
                openSheet(key);
              }}
            />
          ),
          { duration: 6000 },
        );

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.achievementsMine }),
          queryClient.invalidateQueries({ queryKey: queryKeys.achievementsCatalog }),
          queryClient.invalidateQueries({ queryKey: queryKeys.achievementsStats(key) }),
          queryClient.invalidateQueries({ queryKey: queryKeys.achievementsRecentReceivers(key) }),
          queryClient.invalidateQueries({ queryKey: queryKeys.achievementsUser(userId) }),
          // Reward credits + XP land on the user's row — refresh shell widgets
          // so the toast and header counters update together.
          queryClient.invalidateQueries({ queryKey: queryKeys.profile }),
          queryClient.invalidateQueries({ queryKey: queryKeys.xpStatus }),
        ]);
      })();
    });

    return unsubscribe;
  }, [isConnected, channelPrefix, userId, subscribe, queryClient, openSheet, openClaimSheet]);
};
